import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import apiService from '../../services/api';
import {
  Plus,
  Trash2,
  Save,
  X,
  Calendar,
  Layers,
  Package,
  User,
  UserCircle,
} from 'lucide-react';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import RichTextEditor from '../../components/UI/RichTextEditor';
import CreateCustomerModal from '../../components/CreateModals/CreateCustomerModal';
import CreateScheduleModal from '../../components/CreateModals/CreateScheduleModal';
import CreateSectionModal from '../../components/CreateModals/CreateSectionModal';
import CreateProductModal from '../../components/CreateModals/CreateProductModal';

interface Customer {
  _id: string;
  name: string;
  company?: string;
  email?: string;
  phone?: string;
  address?: string;
  projects?: Array<{ name: string; address: string }>;
}

interface Schedule {
  _id: string;
  name: string;
  description?: string;
}

interface Section {
  _id: string;
  name: string;
  description?: string;
  brand?: { _id: string; name: string };
  /** Schedules this section is assigned to (from Section master) */
  schedules?: Array<{ _id: string; name: string }>;
}

interface Product {
  _id: string;
  name: string;
  description?: string;
  unitPrice: number;
  unit?: string | { _id: string; name: string; symbol: string };
  units?: Array<{ _id: string; name?: string; symbol?: string } | string>;
  brand?: string | { _id: string; name: string };
  model?: string;
  specifications?: string;
  sku?: string;
  type?: string | { _id: string; name: string };
  compressorType?: string | { _id: string; name: string };
  countryOfOrigin?: string | { _id: string; name: string };
  countryOfAssembly?: string | { _id: string; name: string };
  details?: string;
  coolingCapacity?: string;
  serialNo?: string;
  partNo?: string;
  parentCategory?: string | { _id: string; name: string };
  subCategory?: string | { _id: string; name: string };
  category?: string | { _id: string; name: string };
  schedules?: Array<{ _id: string; name: string }>;
  /** Single section (catalog assignment) */
  section?: string | { _id: string; name?: string };
  /** Multiple sections (catalog assignment) */
  sections?: Array<{ _id: string; name: string }>;
  image?: string;
}

interface ProfitMargin {
  _id: string;
  name: string;
  rate: number;
}

interface CustomsDuty {
  _id: string;
  name: string;
  rate: number;
}

interface Vat {
  _id: string;
  name: string;
  rate: number;
}

interface Ait {
  _id: string;
  name: string;
  rate: number;
}

interface CategoryItem {
  _id: string;
  name: string;
  parent?: string | { _id: string; name: string } | null;
}

interface ProductRow {
  id: string;
  productId: string;
  productDetails?: Product;
  primaryCategoryId?: string;
  subCategoryId?: string;
  unitPurchaseRate: number;
  qty: number | '';
  unit: string;
  discount: number;
  discountType: 'percentage' | 'amount';
  customsDutyPercentage?: number;
}

interface SectionData {
  id: string;
  sectionId: string;
  sectionDetails?: Section;
  customsDutyPercentage?: number;
  products: ProductRow[];
}

interface ScheduleData {
  id: string;
  scheduleId: string;
  scheduleDetails?: Schedule;
  profitMarginId: string;
  profitMarginRate: number;
  customsDutyId: string;
  customsDutyRate: number;
  other: number;
  aitId: string;
  vatId: string;
  vatInclusion: boolean;
  currency: 'BDT' | 'USD';
  cfrCtg: boolean;
  cfrCtgAmount: number;
  notes: string;
  selectedNoteId: string;
  sections: SectionData[];
}

const CreateQuotationPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Master data
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [profitMargins, setProfitMargins] = useState<ProfitMargin[]>([]);
  const [customsDuties, setCustomsDuties] = useState<CustomsDuty[]>([]);
  const [vats, setVats] = useState<Vat[]>([]);
  const [aits, setAits] = useState<Ait[]>([]);
  const [employees, setEmployees] = useState<Array<{ _id: string; name: string; designation?: string }>>([]);

  // Form data
  const [customerId, setCustomerId] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [inquiryEmployeeId, setInquiryEmployeeId] = useState('');
  const [projectIndex, setProjectIndex] = useState<number>(-1);
  const [selectedProject, setSelectedProject] = useState<{ name: string; address: string } | null>(null);
  const [quotationDate, setQuotationDate] = useState(new Date().toISOString().split('T')[0]);
  const [validUntil, setValidUntil] = useState(
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  
  // New features
  const [quotationsList, setQuotationsList] = useState<any[]>([]);
  const [boqsList, setBoqsList] = useState<any[]>([]);
  const [notesList, setNotesList] = useState<any[]>([]);
  const [termsAndConditionsList, setTermsAndConditionsList] = useState<any[]>([]);
  const [footnotesList, setFootnotesList] = useState<any[]>([]);
  const [selectedNote, setSelectedNote] = useState('');
  const [selectedTermsAndConditions, setSelectedTermsAndConditions] = useState('');
  const [selectedFootnote, setSelectedFootnote] = useState('');
  const [selectedFootnoteId, setSelectedFootnoteId] = useState('');
  const [enableSummary, setEnableSummary] = useState(true);
  const [combined, setCombined] = useState(false);
  const [usdToBdtEnabled, setUsdToBdtEnabled] = useState(false);
  const [usdToBdtRate, setUsdToBdtRate] = useState<number>(0);
  // Searchable product dropdown: which product row has dropdown open
  const [productSearchOpen, setProductSearchOpen] = useState<{ scheduleIdx: number; sectionIdx: number; productIdx: number } | null>(null);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [createCustomerModalOpen, setCreateCustomerModalOpen] = useState(false);
  const [createScheduleModalOpen, setCreateScheduleModalOpen] = useState(false);
  const [createSectionModalOpen, setCreateSectionModalOpen] = useState(false);
  const [createProductModalOpen, setCreateProductModalOpen] = useState(false);

  // Quotation schedules
  const [quotationSchedules, setQuotationSchedules] = useState<ScheduleData[]>([
    {
      id: Date.now().toString(),
      scheduleId: '',
      profitMarginId: '',
      profitMarginRate: 0,
      customsDutyId: '',
      customsDutyRate: 0,
      other: 0,
      aitId: '',
      vatId: '',
      vatInclusion: false,
      currency: 'BDT',
      cfrCtg: false,
      cfrCtgAmount: 0,
      notes: '',
      selectedNoteId: '',
      sections: [],
    },
  ]);

  const fetchAllData = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    try {
      if (!silent) setLoading(true);
      const [
        customersRes,
        schedulesRes,
        sectionsRes,
        categoriesRes,
        productsRes,
        unitsRes,
        profitMarginsRes,
        customsDutiesRes,
        vatsRes,
        aitsRes,
        quotationsRes,
        boqsRes,
        notesRes,
        termsAndConditionsRes,
        footnotesRes,
        employeesRes,
      ] = await Promise.all([
        apiService.getAllCustomers(),
        apiService.getSchedules(),
        apiService.getSections(),
        apiService.getCategories(),
        apiService.getAllQuotationItems(),
        apiService.getUnits(),
        apiService.getProfitMargins(),
        apiService.getCustomsDuties(),
        apiService.getVats(),
        apiService.getAITs(),
        apiService.getAllQuotations(),
        apiService.getAllBOQs(),
        apiService.getNotes(),
        apiService.getTermsAndConditions(),
        apiService.getFootnotes(),
        apiService.getEmployees(),
      ]);

      setCustomers(customersRes.data.customers || customersRes.data || []);
      setSchedules(schedulesRes.data.schedules || schedulesRes.data || []);
      setSections(sectionsRes.data.sections || sectionsRes.data || []);
      setCategories(categoriesRes.data.categories || categoriesRes.data || []);
      setProducts(productsRes.data.items || productsRes.data || []);
      setUnits(unitsRes.data.units || unitsRes.data || []);
      setProfitMargins(profitMarginsRes.data.profitMargins || profitMarginsRes.data || []);
      setCustomsDuties(customsDutiesRes.data.customsDuties || customsDutiesRes.data || []);
      setVats(vatsRes.data.vats || vatsRes.data || []);
      setAits(aitsRes.data.aits || aitsRes.data || []);
      const quotationsRaw = quotationsRes.data || [];
      const boqsRaw = boqsRes.data || [];
      setQuotationsList(
        [...quotationsRaw].sort((a, b) => {
          const dateA = new Date(a.quotationDate || a.createdAt || 0).getTime();
          const dateB = new Date(b.quotationDate || b.createdAt || 0).getTime();
          return dateB - dateA;
        })
      );
      setBoqsList(
        [...boqsRaw].sort((a, b) => {
          const dateA = new Date(a.boqDate || a.createdAt || 0).getTime();
          const dateB = new Date(b.boqDate || b.createdAt || 0).getTime();
          return dateB - dateA;
        })
      );
      setNotesList(notesRes.data || []);
      setTermsAndConditionsList(termsAndConditionsRes.data || []);
      setFootnotesList(footnotesRes.data || []);
      setEmployees(employeesRes.data.employees || employeesRes.data || []);

      // If in edit mode, fetch and populate existing quotation data
      if (isEditMode && id) {
        try {
          const quotationRes = await apiService.getQuotation(id);
          const quotation = quotationRes.data;
          
          // Populate form fields
          setCustomerId(quotation.customer?._id || quotation.customer || '');
          
          // Set project data
          if (quotation.projectName || quotation.projectAddress) {
            setSelectedProject({
              name: quotation.projectName || '',
              address: quotation.projectAddress || ''
            });
          }
          
          setQuotationDate(quotation.quotationDate ? new Date(quotation.quotationDate).toISOString().split('T')[0] : '');
          setValidUntil(quotation.validUntil ? new Date(quotation.validUntil).toISOString().split('T')[0] : '');
          setSelectedNote(quotation.notes || '');
          setSelectedTermsAndConditions(quotation.termsAndConditions || '');
          const fnContent = quotation.footnote || '';
          setSelectedFootnote(fnContent);
          const fnMatch = (footnotesRes.data || []).find((f: any) => (f.content || '') === fnContent);
          setSelectedFootnoteId(fnMatch ? fnMatch._id : '');
          setEnableSummary(quotation.enableSummary !== undefined ? quotation.enableSummary : true);
          setCombined(quotation.combined === true);
          setUsdToBdtEnabled(quotation.usdToBdtEnabled === true);
          setUsdToBdtRate(quotation.usdToBdtRate != null ? Number(quotation.usdToBdtRate) : 0);
          setInquiryEmployeeId(quotation.inquiryOf?._id || quotation.inquiryOf || '');
          
          // If customer is an object, set it
          if (typeof quotation.customer === 'object' && quotation.customer) {
            setSelectedCustomer(quotation.customer);
          }
          
          // Populate schedules
          if (quotation.schedules && quotation.schedules.length > 0) {
            const loadedSchedules = quotation.schedules.map((scheduleData: any) => ({
              id: `${Date.now()}-${Math.random()}`,
              scheduleId: scheduleData.schedule?._id || scheduleData.schedule || '',
              scheduleDetails: scheduleData.schedule || null,
              profitMarginId: scheduleData.profitMargin?._id || scheduleData.profitMargin || '',
              profitMarginRate: scheduleData.profitMarginRate || 0,
              customsDutyId: scheduleData.customsDuty?._id || scheduleData.customsDuty || '',
              customsDutyRate: scheduleData.customsDutyRate || 0,
              other: scheduleData.other || 0,
              aitId: scheduleData.ait?._id || scheduleData.ait || '',
              aitRate: scheduleData.aitRate || 0,
              vatId: scheduleData.vat?._id || scheduleData.vat || '',
              vatRate: scheduleData.vatRate || 0,
              vatInclusion: scheduleData.vatInclusion || false,
              currency: scheduleData.currency || 'BDT',
              cfrCtg: scheduleData.cfrCtg || false,
              cfrCtgAmount: scheduleData.cfrCtgAmount || 0,
              notes: scheduleData.notes || '',
              selectedNoteId: '', // Will be set when note is selected
              sections: scheduleData.sections?.map((sectionData: any) => ({
                id: `${Date.now()}-${Math.random()}`,
                sectionId: sectionData.section?._id || sectionData.section || '',
                sectionDetails: sectionData.section || null,
                customsDutyPercentage: sectionData.customsDutyPercentage,
                products: sectionData.products?.map((productData: any) => {
                  const prod = productData.product;
                  const parentCat = prod?.parentCategory;
                  const subCat = prod?.subCategory;
                  return {
                    id: `${Date.now()}-${Math.random()}`,
                    productId: prod?._id || productData.product || '',
                    productDetails: prod || null,
                    primaryCategoryId: parentCat && typeof parentCat === 'object' ? parentCat._id : (parentCat as string) || '',
                    subCategoryId: subCat && typeof subCat === 'object' ? subCat._id : (subCat as string) || '',
                    unitPurchaseRate: productData.unitPurchaseRate || 0,
                    qty: productData.qty || 1,
                    unit: productData.unit || '',
                    discount: productData.discount || 0,
                    discountType: productData.discountType || 'percentage',
                    customsDutyPercentage: productData.customsDutyPercentage,
                  };
                }) || [],
              })) || [],
            }));
            setQuotationSchedules(loadedSchedules);
          }
        } catch (error) {
          console.error('Error fetching quotation:', error);
          toast.error('Failed to load quotation data');
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (!silent) toast.error('Failed to load data');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [isEditMode, id]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Handle customer selection
  const handleCustomerChange = (customerIdValue: string) => {
    setCustomerId(customerIdValue);
    const customer = customers.find((c) => c._id === customerIdValue);
    setSelectedCustomer(customer || null);
    setProjectIndex(-1);
    setSelectedProject(null);
  };

  // Handle project selection
  const handleProjectChange = (index: number) => {
    setProjectIndex(index);
    if (selectedCustomer && selectedCustomer.projects && index >= 0) {
      setSelectedProject(selectedCustomer.projects[index]);
    } else {
      setSelectedProject(null);
    }
  };

  // Handle load from previous quotation
  const handleLoadFromQuotation = async (quotationId: string) => {
    if (!quotationId) return;
    
    try {
      const response = await apiService.getQuotation(quotationId);
      const quotation = response.data;
      
      // Populate customer
      if (quotation.customer?._id) {
        setCustomerId(quotation.customer._id);
        setSelectedCustomer(quotation.customer);
      }
      setInquiryEmployeeId(quotation.inquiryOf?._id || quotation.inquiryOf || '');
      
      // Populate notes and T&C
      if (quotation.notes) {
        setSelectedNote(quotation.notes);
      }
      if (quotation.termsAndConditions) {
        setSelectedTermsAndConditions(quotation.termsAndConditions);
      }
      if (quotation.footnote) {
        setSelectedFootnote(quotation.footnote);
        const fnMatch = footnotesList.find((f: any) => (f.content || '') === quotation.footnote);
        setSelectedFootnoteId(fnMatch ? fnMatch._id : '');
      }
      
      // Populate schedules with all data
      if (quotation.schedules && quotation.schedules.length > 0) {
        const loadedSchedules = quotation.schedules.map((sch: any) => ({
          id: Date.now().toString() + Math.random(),
          scheduleId: sch.schedule?._id || sch.schedule || '',
          scheduleDetails: schedules.find((s) => s._id === (sch.schedule?._id || sch.schedule)),
          profitMarginId: sch.profitMargin?._id || sch.profitMargin || '',
          profitMarginRate: sch.profitMarginRate ?? 0,
          customsDutyId: sch.customsDuty?._id || sch.customsDuty || '',
          customsDutyRate: sch.customsDutyRate ?? 0,
          other: sch.other || 0,
          aitId: sch.ait?._id || sch.ait || '',
          vatId: sch.vat?._id || sch.vat || '',
          vatInclusion: sch.vatInclusion || false,
          currency: sch.currency || 'BDT',
          cfrCtg: sch.cfrCtg || false,
          cfrCtgAmount: sch.cfrCtgAmount || 0,
          notes: sch.notes || '',
          selectedNoteId: '', // Will be set when note is selected
          sections: sch.sections.map((sec: any) => ({
            id: Date.now().toString() + Math.random(),
            sectionId: sec.section?._id || sec.section || '',
            sectionDetails: sections.find((s) => s._id === (sec.section?._id || sec.section)),
            customsDutyPercentage: sec.customsDutyPercentage,
            products: sec.products.map((prod: any) => {
              const prodDetails = products.find((p) => p._id === (prod.product?._id || prod.product));
              const parentCat = prodDetails?.parentCategory;
              const subCat = prodDetails?.subCategory;
              return {
                id: Date.now().toString() + Math.random(),
                productId: prod.product?._id || prod.product || '',
                productDetails: prodDetails,
                primaryCategoryId: parentCat && typeof parentCat === 'object' ? parentCat._id : (parentCat as string) || '',
                subCategoryId: subCat && typeof subCat === 'object' ? subCat._id : (subCat as string) || '',
                unitPurchaseRate: prod.unitPurchaseRate || 0,
                qty: prod.qty || 1,
                unit: prod.unit || '',
                discount: prod.discount || 0,
                discountType: prod.discountType || 'percentage',
                customsDutyPercentage: prod.customsDutyPercentage,
              };
            }),
          })),
        }));
        setQuotationSchedules(loadedSchedules);
      }
      
      toast.success('Loaded data from previous quotation');
    } catch (error) {
      console.error('Error loading quotation:', error);
      toast.error('Failed to load quotation');
    }
  };

  // Handle load from BOQ
  const handleLoadFromBOQ = async (boqId: string) => {
    if (!boqId) return;
    
    try {
      const response = await apiService.getBOQ(boqId);
      const boq = response.data;
      
      // Populate customer
      if (boq.customer?._id) {
        setCustomerId(boq.customer._id);
        setSelectedCustomer(boq.customer);
      }
      setInquiryEmployeeId(boq.inquiryOf?._id || boq.inquiryOf || '');
      
      // Populate notes and T&C if they exist
      if (boq.notes) {
        setSelectedNote(boq.notes);
      }
      if (boq.termsAndConditions) {
        setSelectedTermsAndConditions(boq.termsAndConditions);
      if (boq.footnote) {
        setSelectedFootnote(boq.footnote);
        const fnMatch = footnotesList.find((f: any) => (f.content || '') === boq.footnote);
        setSelectedFootnoteId(fnMatch ? fnMatch._id : '');
      }
      }
      
      // Populate schedules from BOQ (without pricing, user will need to add pricing)
      if (boq.schedules && boq.schedules.length > 0) {
        const loadedSchedules = boq.schedules.map((sch: any) => ({
          id: Date.now().toString() + Math.random(),
          scheduleId: sch.schedule?._id || sch.schedule || '',
          scheduleDetails: schedules.find((s) => s._id === (sch.schedule?._id || sch.schedule)),
          profitMarginId: '',
          profitMarginRate: 0,
          customsDutyId: '',
          customsDutyRate: 0,
          other: 0,
          aitId: '',
          vatId: '',
          vatInclusion: false,
          currency: 'BDT',
          sections: sch.sections.map((sec: any) => ({
            id: Date.now().toString() + Math.random(),
            sectionId: sec.section?._id || sec.section || '',
            sectionDetails: sections.find((s) => s._id === (sec.section?._id || sec.section)),
            customsDutyPercentage: undefined,
            products: sec.products.map((prod: any) => {
              const productDetails = products.find((p) => p._id === (prod.product?._id || prod.product));
              const parentCat = productDetails?.parentCategory;
              const subCat = productDetails?.subCategory;
              return {
                id: Date.now().toString() + Math.random(),
                productId: prod.product?._id || prod.product || '',
                productDetails: productDetails,
                primaryCategoryId: parentCat && typeof parentCat === 'object' ? parentCat._id : (parentCat as string) || '',
                subCategoryId: subCat && typeof subCat === 'object' ? subCat._id : (subCat as string) || '',
                unitPurchaseRate: productDetails?.unitPrice || 0,
                qty: prod.qty || 1,
                unit: prod.unit || '',
                discount: 0,
                discountType: 'percentage' as 'percentage' | 'amount',
                customsDutyPercentage: undefined,
              };
            }),
          })),
        }));
        setQuotationSchedules(loadedSchedules);
      }
      
      toast.success('Loaded data from BOQ - please add pricing details');
    } catch (error) {
      console.error('Error loading BOQ:', error);
      toast.error('Failed to load BOQ');
    }
  };

  // Handle schedule change
  const handleScheduleChange = (scheduleIdx: number, scheduleIdValue: string) => {
    const schedule = schedules.find((s) => s._id === scheduleIdValue);
    const newSchedules = [...quotationSchedules];
    newSchedules[scheduleIdx].scheduleId = scheduleIdValue;
    newSchedules[scheduleIdx].scheduleDetails = schedule;
    setQuotationSchedules(newSchedules);
  };

  // Add new schedule
  const addSchedule = () => {
    setQuotationSchedules([
      ...quotationSchedules,
      {
        id: Date.now().toString(),
        scheduleId: '',
        profitMarginId: '',
        profitMarginRate: 0,
        customsDutyId: '',
        customsDutyRate: 0,
        other: 0,
        aitId: '',
        vatId: '',
        vatInclusion: false,
        currency: 'BDT',
        cfrCtg: false,
        cfrCtgAmount: 0,
        notes: '',
        selectedNoteId: '',
        sections: [],
      },
    ]);
  };

  // Remove schedule
  const removeSchedule = (scheduleIdx: number) => {
    if (quotationSchedules.length === 1) {
      toast.error('At least one schedule is required');
      return;
    }
    const newSchedules = quotationSchedules.filter((_, idx) => idx !== scheduleIdx);
    setQuotationSchedules(newSchedules);
  };

  // Update schedule percentage fields
  const updateScheduleField = (scheduleIdx: number, field: string, value: any) => {
    const newSchedules = [...quotationSchedules];
    (newSchedules[scheduleIdx] as any)[field] = value;
    setQuotationSchedules(newSchedules);
  };

  // Add section to schedule
  const addSection = (scheduleIdx: number) => {
    const newSchedules = [...quotationSchedules];
    newSchedules[scheduleIdx].sections.push({
      id: Date.now().toString(),
      sectionId: '',
      customsDutyPercentage: undefined,
      products: [],
    });
    setQuotationSchedules(newSchedules);
  };

  // Update section field (e.g. customsDutyPercentage)
  const updateSectionField = (scheduleIdx: number, sectionIdx: number, field: string, value: any) => {
    const newSchedules = [...quotationSchedules];
    (newSchedules[scheduleIdx].sections[sectionIdx] as any)[field] = value;
    setQuotationSchedules(newSchedules);
  };

  // Remove section
  const removeSection = (scheduleIdx: number, sectionIdx: number) => {
    const newSchedules = [...quotationSchedules];
    newSchedules[scheduleIdx].sections = newSchedules[scheduleIdx].sections.filter(
      (_, idx) => idx !== sectionIdx
    );
    setQuotationSchedules(newSchedules);
  };

  // Sections that belong to the given schedule (Section master assigns sections to schedules)
  const getSectionsForSchedule = (scheduleId: string): Section[] =>
    !scheduleId ? [] : sections.filter((sec) => sec.schedules?.some((s) => (s._id || (s as any)) === scheduleId));

  // Handle section change
  const handleSectionChange = (scheduleIdx: number, sectionIdx: number, sectionIdValue: string) => {
    const section = sections.find((s) => s._id === sectionIdValue);
    const newSchedules = [...quotationSchedules];
    newSchedules[scheduleIdx].sections[sectionIdx].sectionId = sectionIdValue;
    newSchedules[scheduleIdx].sections[sectionIdx].sectionDetails = section;
    // Clear products that are not assigned to the new section in catalog
    const allowedIds = getProductsForSection(sectionIdValue).map((p) => p._id);
    newSchedules[scheduleIdx].sections[sectionIdx].products.forEach((row) => {
      if (row.productId && !allowedIds.includes(row.productId)) {
        row.productId = '';
        row.productDetails = undefined;
      }
    });
    setQuotationSchedules(newSchedules);
  };

  // Add product to section
  const addProduct = (scheduleIdx: number, sectionIdx: number) => {
    const newSchedules = [...quotationSchedules];
    newSchedules[scheduleIdx].sections[sectionIdx].products.push({
      id: Date.now().toString() + Math.random(),
      productId: '',
      primaryCategoryId: '',
      subCategoryId: '',
      unitPurchaseRate: 0,
      qty: 1,
      unit: '',
      discount: 0,
      discountType: 'percentage',
      customsDutyPercentage: undefined,
    });
    setQuotationSchedules(newSchedules);
  };

  // Remove product
  const removeProduct = (scheduleIdx: number, sectionIdx: number, productIdx: number) => {
    const newSchedules = [...quotationSchedules];
    newSchedules[scheduleIdx].sections[sectionIdx].products = newSchedules[scheduleIdx].sections[
      sectionIdx
    ].products.filter((_, idx) => idx !== productIdx);
    setQuotationSchedules(newSchedules);
  };

  // Handle primary category change: clear sub category and product if product not in new filter
  const handlePrimaryCategoryChange = (
    scheduleIdx: number,
    sectionIdx: number,
    productIdx: number,
    primaryCategoryId: string
  ) => {
    const newSchedules = [...quotationSchedules];
    const row = newSchedules[scheduleIdx].sections[sectionIdx].products[productIdx];
    row.primaryCategoryId = primaryCategoryId;
    row.subCategoryId = '';
    const filtered = getFilteredProducts(primaryCategoryId, '');
    if (row.productId && !filtered.some((p) => p._id === row.productId)) {
      row.productId = '';
      row.productDetails = undefined;
      row.unit = '';
      row.unitPurchaseRate = 0;
    }
    setQuotationSchedules(newSchedules);
    setProductSearchOpen(null);
    setProductSearchQuery('');
  };

  // Handle sub category change: clear product if not in new filter
  const handleSubCategoryChange = (
    scheduleIdx: number,
    sectionIdx: number,
    productIdx: number,
    subCategoryId: string
  ) => {
    const newSchedules = [...quotationSchedules];
    const row = newSchedules[scheduleIdx].sections[sectionIdx].products[productIdx];
    row.subCategoryId = subCategoryId;
    const primaryId = row.primaryCategoryId || '';
    const filtered = getFilteredProducts(primaryId, subCategoryId);
    if (row.productId && !filtered.some((p) => p._id === row.productId)) {
      row.productId = '';
      row.productDetails = undefined;
      row.unit = '';
      row.unitPurchaseRate = 0;
    }
    setQuotationSchedules(newSchedules);
    setProductSearchOpen(null);
    setProductSearchQuery('');
  };

  // Handle product change
  const handleProductChange = (
    scheduleIdx: number,
    sectionIdx: number,
    productIdx: number,
    productIdValue: string
  ) => {
    const product = products.find((p) => p._id === productIdValue);
    const newSchedules = [...quotationSchedules];
    const productRow = newSchedules[scheduleIdx].sections[sectionIdx].products[productIdx];
    productRow.productId = productIdValue;
    productRow.productDetails = product;
    productRow.unitPurchaseRate = product?.unitPrice || 0;
    
    // Auto-fill unit: one unit → autofill; multiple units → set first, user picks from dropdown
    let unitValue = '';
    if (product) {
      const { singleUnit, options } = getProductUnitOptions(product, units);
      if (singleUnit) {
        unitValue = singleUnit;
      } else if (options.length > 0) {
        unitValue = options[0].value;
      }
    }
    productRow.unit = unitValue;
    setQuotationSchedules(newSchedules);
  };

  // Update product field
  const updateProductField = (
    scheduleIdx: number,
    sectionIdx: number,
    productIdx: number,
    field: string,
    value: any
  ) => {
    const newSchedules = [...quotationSchedules];
    (newSchedules[scheduleIdx].sections[sectionIdx].products[productIdx] as any)[field] = value;
    setQuotationSchedules(newSchedules);
  };

  // Calculate product totals (section optional: for product/section-level customs % override)
  const calculateProductTotals = (scheduleIdx: number, productRow: ProductRow, section?: SectionData) => {
    const schedule = quotationSchedules[scheduleIdx];
    const profitMargin = profitMargins.find((pm) => pm._id === schedule.profitMarginId);
    const customsDuty = customsDuties.find((cd) => cd._id === schedule.customsDutyId);
    const ait = aits.find((a) => a._id === schedule.aitId);
    const vat = vats.find((v) => v._id === schedule.vatId);

    const baseRate = productRow.unitPurchaseRate;
    const profitRate = schedule.profitMarginRate ?? profitMargin?.rate ?? 0;
    // Customs: schedule rate + section % + product % (all additive)
    const scheduleCustomsRate = schedule.customsDutyRate ?? customsDuty?.rate ?? 0;
    const sectionCustomsPct = section?.customsDutyPercentage ?? 0;
    const productCustomsPct = productRow.customsDutyPercentage ?? 0;
    const customsRate = scheduleCustomsRate + sectionCustomsPct + productCustomsPct;
    
    // Step 1: Calculate profit margin on base rate
    const profitMarginAmount = (baseRate * profitRate) / 100;
    
    // Step 2: Calculate customs & freight on (base rate + profit margin) using combined customs %
    const baseWithProfit = baseRate + profitMarginAmount;
    const customsDutyAmount = (baseWithProfit * customsRate) / 100;
    
    // Step 3: Calculate others on (base rate + profit margin)
    const otherAmount = (baseWithProfit * (schedule.other || 0)) / 100;
    
    // Step 4: Calculate AIT on (base rate + profit margin + customs + others)
    const baseWithProfitCustomsOthers = baseRate + profitMarginAmount + customsDutyAmount + otherAmount;
    const aitAmount = (baseWithProfitCustomsOthers * (ait?.rate || 0)) / 100;

    // Step 5: Calculate unit rate (base + profit + customs + others + AIT)
    const unitRate = baseRate + profitMarginAmount + customsDutyAmount + otherAmount + aitAmount;
    
    // Step 6: Calculate VAT on unit rate (after adding AIT)
    const vatOnRate = (unitRate * (vat?.rate || 0)) / 100;
    
    const qtyNum = Number(productRow.qty);
    const qty = Number.isFinite(qtyNum) && qtyNum >= 0 ? qtyNum : 0;
    // VAT Amount is always calculated as vatOnRate * qty
    const vatAmount = vatOnRate * qty;
    
    // Step 7: Calculate total based on VAT inclusion
    let subtotal: number;
    if (schedule.vatInclusion) {
      // If VAT inclusion is enabled: Total = (Unit Rate + VAT) * Qty
    const unitRateWithVat = unitRate + vatOnRate;
      subtotal = unitRateWithVat * qty;
    } else {
      // If VAT inclusion is disabled: Total = Unit Rate * Qty
      subtotal = unitRate * qty;
    }

    const discountAmount =
      productRow.discountType === 'percentage'
        ? (subtotal * productRow.discount) / 100
        : productRow.discount;
    const total = subtotal - discountAmount;

    return {
      profitMarginAmount,
      customsDutyAmount,
      otherAmount,
      aitAmount,
      unitRate,
      vatOnRate,
      total,
      vatAmount,
    };
  };

  // Convert number to words
  const numberToWords = (num: number): string => {
    if (num === 0) return 'Zero';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    const convertLessThanThousand = (n: number): string => {
      if (n === 0) return '';
      
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
      
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '');
    };
    
    // Split into integer and decimal parts
    const [integerPart, decimalPart] = num.toFixed(2).split('.');
    const intNum = parseInt(integerPart);
    
    if (intNum === 0) {
      return 'Zero Taka' + (parseInt(decimalPart) > 0 ? ` and ${decimalPart} Paisa` : ' Only');
    }
    
    let word = '';
    
    // Crore
    if (intNum >= 10000000) {
      word += convertLessThanThousand(Math.floor(intNum / 10000000)) + ' Crore ';
    }
    
    // Lakh
    const lakhPart = Math.floor((intNum % 10000000) / 100000);
    if (lakhPart > 0) {
      word += convertLessThanThousand(lakhPart) + ' Lakh ';
    }
    
    // Thousand
    const thousandPart = Math.floor((intNum % 100000) / 1000);
    if (thousandPart > 0) {
      word += convertLessThanThousand(thousandPart) + ' Thousand ';
    }
    
    // Hundred
    const hundredPart = intNum % 1000;
    if (hundredPart > 0) {
      word += convertLessThanThousand(hundredPart) + ' ';
    }
    
    word = word.trim() + ' Taka';
    
    // Add paisa if decimal part exists
    if (parseInt(decimalPart) > 0) {
      word += ` and ${convertLessThanThousand(parseInt(decimalPart))} Paisa`;
    }
    
    return word + ' Only';
  };

  // Calculate schedule totals
  const calculateScheduleTotals = (scheduleIdx: number) => {
    const schedule = quotationSchedules[scheduleIdx];
    let totalAmount = 0;
    let totalVatAmount = 0;
    
    schedule.sections.forEach((section) => {
      section.products.forEach((product) => {
        const calc = calculateProductTotals(scheduleIdx, product, section);
        totalAmount += calc.total;
        totalVatAmount += calc.vatAmount;
      });
    });
    
    // Add CFR CTG amount if it exists
    const cfrCtgAmount = schedule.cfrCtgAmount || 0;
    // Base total: totalAmount (which may or may not include VAT depending on vatInclusion)
    // If VAT is included, totalAmount already has VAT, so we just add CFR CTG
    // If VAT is not included, we add totalAmount + totalVatAmount + CFR CTG
    const baseTotal = schedule.vatInclusion ? totalAmount : (totalAmount + totalVatAmount);
    const grandTotal = baseTotal + cfrCtgAmount;
    
    return {
      totalAmount,
      totalVatAmount,
      cfrCtgAmount,
      grandTotal,
    };
  };

  // Calculate schedule-based quotation summary
  const calculateQuotationSummary = () => {
    const schedulesSummary = quotationSchedules.map((schedule, scheduleIdx) => {
      const scheduleTotals = calculateScheduleTotals(scheduleIdx);
      
      let scheduleSubtotal = 0;
      schedule.sections.forEach((section) => {
        section.products.forEach((product) => {
          const qtyNum = Number(product.qty);
          scheduleSubtotal += product.unitPurchaseRate * (Number.isFinite(qtyNum) && qtyNum >= 0 ? qtyNum : 0);
        });
      });
      
      return {
        scheduleName: schedule.scheduleDetails?.name || `Schedule ${scheduleIdx + 1}`,
        currency: schedule.currency || 'BDT',
        subtotal: scheduleSubtotal,
        totalAmount: scheduleTotals.totalAmount,
        vatAmount: scheduleTotals.totalVatAmount,
        grandTotal: scheduleTotals.grandTotal,
      };
    });
    
    // Calculate overall totals by currency
    const overallByCurrency = {
      BDT: { subtotal: 0, totalAmount: 0, vatAmount: 0, grandTotal: 0 },
      USD: { subtotal: 0, totalAmount: 0, vatAmount: 0, grandTotal: 0 },
    };
    
    schedulesSummary.forEach((schedule) => {
      const curr = schedule.currency;
      overallByCurrency[curr].subtotal += schedule.subtotal;
      overallByCurrency[curr].totalAmount += schedule.totalAmount;
      overallByCurrency[curr].vatAmount += schedule.vatAmount;
      overallByCurrency[curr].grandTotal += schedule.grandTotal;
    });
    
    return {
      schedules: schedulesSummary,
      overallByCurrency,
    };
  };

  // Primary categories (no parent)
  const primaryCategories = React.useMemo(
    () =>
      categories.filter((c) => {
        const p = c.parent;
        return p == null || p === '' || (typeof p === 'string' && !p);
      }),
    [categories]
  );

  // Sub categories under a primary (parent._id === primaryId)
  const getSubCategoriesForPrimary = (primaryId: string) =>
    categories.filter((c) => {
      const p = c.parent;
      if (!p) return false;
      const parentId = typeof p === 'object' && p !== null ? p._id : p;
      return parentId === primaryId;
    });

  // Catalog items assigned to the given section (section/sections on catalog page)
  const getProductsForSection = (sectionId: string): Product[] => {
    if (!sectionId) return products;
    return products.filter((p) => {
      const singleId = p.section != null && (typeof p.section === 'object' ? p.section._id : p.section);
      const inSingle = singleId === sectionId;
      const inMulti = (p.sections || []).some((s) => (s._id || (s as any)) === sectionId);
      return inSingle || inMulti;
    });
  };

  // Get products filtered by primary category and/or sub category (optional pool, default all products)
  const getFilteredProducts = (primaryCategoryId?: string, subCategoryId?: string, productPool?: Product[]): Product[] => {
    const pool = productPool ?? products;
    if (!primaryCategoryId && !subCategoryId) return pool;
    return pool.filter((p) => {
      const pParentId = p.parentCategory && typeof p.parentCategory === 'object' ? p.parentCategory._id : (p.parentCategory as string) || '';
      const pSubId = p.subCategory && typeof p.subCategory === 'object' ? p.subCategory._id : (p.subCategory as string) || '';
      if (subCategoryId) return pSubId === subCategoryId;
      if (primaryCategoryId) return pParentId === primaryCategoryId;
      return true;
    });
  };

  /** Get unit options for a product: if product has one unit → single display value; if multiple → list of { value, label }. */
  const getProductUnitOptions = (product: Product | undefined, unitsList: any[]): { singleUnit: string | null; options: { value: string; label: string }[] } => {
    if (!product || !unitsList?.length) return { singleUnit: null, options: unitsList?.map((u) => ({ value: u.symbol || u.name || '', label: u.symbol ? `${u.name} (${u.symbol})` : u.name || '' })) || [] };
    const arr = Array.isArray(product.units) ? product.units : [];
    if (arr.length > 0) {
      const options = arr.map((u: any) => {
        const id = u?._id || u;
        const uObj = unitsList.find((ul) => String(ul._id) === String(id));
        const value = uObj ? (uObj.symbol || uObj.name || '') : (typeof u === 'object' && u != null ? u.symbol || u.name || '' : String(u));
        const label = uObj ? (uObj.symbol ? `${uObj.name} (${uObj.symbol})` : uObj.name || '') : value;
        return { value, label };
      }).filter((o: { value: string; label: string }) => o.value);
      if (options.length === 1) return { singleUnit: options[0].value, options: [] };
      if (options.length > 1) return { singleUnit: null, options };
    }
    const single = product.unit;
    if (!single) return { singleUnit: null, options: unitsList.map((u) => ({ value: u.symbol || u.name || '', label: u.symbol ? `${u.name} (${u.symbol})` : u.name || '' })) };
    const unitId = typeof single === 'object' && single != null ? single._id : single;
    const uObj = unitsList.find((ul) => String(ul._id) === String(unitId));
    const display = uObj ? (uObj.symbol || uObj.name || '') : (typeof single === 'object' && single != null ? single.symbol || single.name || '' : String(single));
    return { singleUnit: display, options: [] };
  };

  // Handle save
  const handleSave = async () => {
    if (!customerId) {
      toast.error('Please select a customer');
      return;
    }

    // Validate schedules
    if (quotationSchedules.length === 0 || !quotationSchedules[0].scheduleId) {
      toast.error('Please add at least one schedule');
      return;
    }

    // Validate that schedules have sections
    for (let i = 0; i < quotationSchedules.length; i++) {
      const schedule = quotationSchedules[i];
      if (!schedule.scheduleId) {
        toast.error(`Schedule ${i + 1}: Please select a schedule`);
        return;
      }
      if (schedule.sections.length === 0) {
        toast.error(`Schedule ${i + 1}: Please add at least one section`);
        return;
      }
      
      // Validate sections have products
      for (let j = 0; j < schedule.sections.length; j++) {
        const section = schedule.sections[j];
        if (!section.sectionId) {
          toast.error(`Schedule ${i + 1}, Section ${j + 1}: Please select a section`);
          return;
        }
        if (section.products.length === 0) {
          toast.error(`Schedule ${i + 1}, Section ${j + 1}: Please add at least one product`);
          return;
        }
        
        // Validate products
        for (let k = 0; k < section.products.length; k++) {
          const product = section.products[k];
          if (!product.productId) {
            toast.error(`Schedule ${i + 1}, Section ${j + 1}, Product ${k + 1}: Please select a product`);
            return;
          }
        }
      }
    }

    try {
      setSaving(true);

      // Format schedules data
      const formattedSchedules = quotationSchedules.map((schedule) => {
        const profitMargin = profitMargins.find((pm) => pm._id === schedule.profitMarginId);
        const customsDuty = customsDuties.find((cd) => cd._id === schedule.customsDutyId);
        const ait = aits.find((a) => a._id === schedule.aitId);
        const vat = vats.find((v) => v._id === schedule.vatId);

        return {
          schedule: schedule.scheduleId,
          scheduleName: schedule.scheduleDetails?.name,
          scheduleDescription: schedule.scheduleDetails?.description,
          profitMargin: schedule.profitMarginId || undefined,
          profitMarginRate: schedule.profitMarginRate ?? profitMargin?.rate ?? 0,
          customsDuty: schedule.customsDutyId || undefined,
          customsDutyRate: schedule.customsDutyRate ?? customsDuty?.rate ?? 0,
          other: schedule.other || 0,
          ait: schedule.aitId || undefined,
          aitRate: ait?.rate || 0,
          vat: schedule.vatId || undefined,
          vatRate: vat?.rate || 0,
          vatInclusion: schedule.vatInclusion,
          currency: schedule.currency,
          cfrCtg: schedule.cfrCtg || false,
          cfrCtgAmount: schedule.cfrCtgAmount || 0,
          notes: schedule.notes,
          sections: schedule.sections.map((section) => ({
            section: section.sectionId,
            sectionName: section.sectionDetails?.name,
            sectionDescription: section.sectionDetails?.description,
            customsDutyPercentage: section.customsDutyPercentage,
            products: section.products.map((product) => {
              const calc = calculateProductTotals(
                quotationSchedules.indexOf(schedule),
                product,
                section
              );
              return {
                product: product.productId,
                name: product.productDetails?.name,
                description: product.productDetails?.description,
                brand: product.productDetails?.brand,
                model: product.productDetails?.model,
                specifications: product.productDetails?.specifications,
                unitPurchaseRate: product.unitPurchaseRate,
                qty: (() => { const n = Number(product.qty); return Number.isFinite(n) && n >= 0 ? n : 0; })(),
                unit: product.unit,
                discount: product.discount,
                discountType: product.discountType,
                customsDutyPercentage: product.customsDutyPercentage,
                profitMarginAmount: calc.profitMarginAmount,
                customsDutyAmount: calc.customsDutyAmount,
                otherAmount: calc.otherAmount,
                aitAmount: calc.aitAmount,
                unitRate: calc.unitRate,
                vatOnRate: calc.vatOnRate,
                total: calc.total,
                vatAmount: calc.vatAmount,
              };
            }),
          })),
        };
      });

      // Calculate overall totals
      let grandTotal = 0;
      let grandSubtotal = 0;
      formattedSchedules.forEach((schedule) => {
        schedule.sections.forEach((section) => {
          section.products.forEach((product) => {
            grandTotal += product.total || 0;
            const qtyNum = Number(product.qty);
            grandSubtotal += product.unitPurchaseRate * (Number.isFinite(qtyNum) && qtyNum >= 0 ? qtyNum : 0);
          });
        });
      });

      const inquiryEmployee = employees.find((e) => e._id === inquiryEmployeeId);
      // Prepare quotation data
      const quotationData: any = {
        customer: customerId,
        inquiryOf: inquiryEmployeeId || undefined,
        inquiryOfDesignation: inquiryEmployee?.designation || undefined,
        projectName: selectedProject?.name,
        projectAddress: selectedProject?.address,
        quotationDate: new Date(quotationDate).toISOString(),
        validUntil: new Date(validUntil).toISOString(),
        schedules: formattedSchedules,
        subtotal: grandSubtotal,
        total: grandTotal,
        notes: selectedNote,
        termsAndConditions: selectedTermsAndConditions,
        footnote: selectedFootnote,
        status: 'draft',
      };
      
      // Explicitly set enableSummary, combined, USD conversion to ensure false/0 values are included
      quotationData.enableSummary = enableSummary;
      quotationData.combined = combined;
      quotationData.usdToBdtEnabled = usdToBdtEnabled;
      quotationData.usdToBdtRate = usdToBdtRate;

      // Save to backend
      if (isEditMode && id) {
        await apiService.updateQuotation(id, quotationData);
        toast.success('Quotation updated successfully!');
      } else {
        await apiService.createQuotation(quotationData);
        toast.success('Quotation created successfully!');
      }
      
      navigate('/quotations');
    } catch (error: any) {
      console.error('Error saving quotation:', error);
      
      // Display detailed error message
      const errorMessage = error.response?.data?.details
        || error.response?.data?.message
        || (error.response?.data?.errors?.length > 0 
            ? (typeof error.response.data.errors[0] === 'string' 
                ? error.response.data.errors.join(', ')
                : error.response.data.errors.map((e: any) => e.msg || e.message).join(', '))
            : null)
        || error.response?.data?.error
        || 'Failed to save quotation';

      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-3 pb-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between bg-white border-b border-gray-200 -mx-6 px-6 -mt-6 py-3 sticky top-0 z-10 shadow-sm"
      >
        <div>
          <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
            {isEditMode ? 'Edit Quotation' : 'Create Quotation'}
          </h1>
          <p className="text-xs text-gray-500" style={{ fontFamily: 'var(--font-family)' }}>
            Build a comprehensive quotation with schedules, sections, and products
          </p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/quotations')} icon={<X className="w-3 h-3" />}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} loading={saving} icon={<Save className="w-3 h-3" />}>
            Save Quotation
          </Button>
        </div>
      </motion.div>

      {/* Quotation Information */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
        <Card padding="sm" className="overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Quotation No.</label>
              <input
                type="text"
                value="Will be auto-generated (QT-1001)"
                readOnly
                className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-500"
                style={{ fontFamily: 'var(--font-family)' }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Quotation Date</label>
              <div className="relative">
                <Calendar className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
                <input
                  type="date"
                  value={quotationDate}
                  onChange={(e) => setQuotationDate(e.target.value)}
                  className="block w-full pl-7 px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  style={{ fontFamily: 'var(--font-family)' }}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Valid Until</label>
              <div className="relative">
                <Calendar className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
                <input
                  type="date"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                  className="block w-full pl-7 px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  style={{ fontFamily: 'var(--font-family)' }}
                />
              </div>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Quotation Options */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card padding="sm" className="overflow-hidden">
          <div className="flex items-center mb-2 pb-2 border-b border-gray-200">
            <Layers className="w-4 h-4 text-blue-600 mr-2" />
            <h2 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
              Quotation Options
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {/* Load from Previous Quotation */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Load from Previous Quotation
              </label>
              <select
                onChange={(e) => handleLoadFromQuotation(e.target.value)}
                className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                style={{ fontFamily: 'var(--font-family)' }}
                defaultValue=""
              >
                <option value="">Select a quotation</option>
                {quotationsList.map((quot) => (
                  <option key={quot._id} value={quot._id}>
                    {quot.quotationNumber} - {quot.customer?.name || quot.customer?.company || 'N/A'}
                  </option>
                ))}
              </select>
            </div>

            {/* Load from BOQ */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Load from BOQ
              </label>
              <select
                onChange={(e) => handleLoadFromBOQ(e.target.value)}
                className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                style={{ fontFamily: 'var(--font-family)' }}
                defaultValue=""
              >
                <option value="">Select a BOQ</option>
                {boqsList.map((boq) => (
                  <option key={boq._id} value={boq._id}>
                    {boq.boqNumber} - {boq.customer?.name || boq.customer?.company || 'N/A'}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes Dropdown */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Notes
              </label>
              <select
                value={selectedNote}
                onChange={(e) => {
                  const note = notesList.find(n => n._id === e.target.value);
                  setSelectedNote(note ? note.content : '');
                }}
                className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                style={{ fontFamily: 'var(--font-family)' }}
              >
                <option value="">Select a note (optional)</option>
                {notesList.map((note) => (
                  <option key={note._id} value={note._id}>
                    {note.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Terms & Conditions Dropdown */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Terms & Conditions
              </label>
              <select
                value={selectedTermsAndConditions}
                onChange={(e) => {
                  const tac = termsAndConditionsList.find(t => t._id === e.target.value);
                  setSelectedTermsAndConditions(tac ? tac.content : '');
                }}
                className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                style={{ fontFamily: 'var(--font-family)' }}
              >
                <option value="">Select terms & conditions (optional)</option>
                {termsAndConditionsList.map((tac) => (
                  <option key={tac._id} value={tac._id}>
                    {tac.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Footnote Dropdown */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Footnote
              </label>
              <select
                value={selectedFootnoteId}
                onChange={(e) => {
                  const fn = footnotesList.find(f => f._id === e.target.value);
                  setSelectedFootnoteId(e.target.value);
                  setSelectedFootnote(fn ? fn.content : '');
                }}
                className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                style={{ fontFamily: 'var(--font-family)' }}
              >
                <option value="">Select footnote (optional)</option>
                {footnotesList.map((fn) => (
                  <option key={fn._id} value={fn._id}>
                    {fn.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Enable Summary Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="enableSummary"
                checked={enableSummary}
                onChange={(e) => setEnableSummary(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="enableSummary" className="ml-2 text-xs font-medium text-gray-700">
                Enable Summary
              </label>
            </div>

            {/* Combined Toggle */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="combined"
                checked={combined}
                onChange={(e) => setCombined(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="combined" className="ml-2 text-xs font-medium text-gray-700">
                Combined (view/print as single A4 landscape table)
              </label>
            </div>

            {/* USD to BDT Conversion */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="usdToBdtEnabled"
                checked={usdToBdtEnabled}
                onChange={(e) => setUsdToBdtEnabled(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="usdToBdtEnabled" className="text-xs font-medium text-gray-700">
                USD to BDT conversion
              </label>
              {usdToBdtEnabled && (
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={usdToBdtRate || ''}
                  onChange={(e) => setUsdToBdtRate(e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
                  placeholder="Rate"
                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded"
                />
              )}
            </div>
          </div>

          {/* Display Selected Notes */}
          {selectedNote && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Selected Note (Editable)
              </label>
              <textarea
                value={selectedNote}
                onChange={(e) => setSelectedNote(e.target.value)}
                rows={3}
                className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                style={{ fontFamily: 'var(--font-family)' }}
              />
            </div>
          )}

          {/* Display Selected Terms & Conditions */}
          {selectedTermsAndConditions && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Selected Terms & Conditions (Editable)
              </label>
              <RichTextEditor
                value={selectedTermsAndConditions}
                onChange={setSelectedTermsAndConditions}
                placeholder="Edit terms and conditions (use toolbar for bold, italic, underline, strikethrough)"
                minHeight="120px"
              />
            </div>
          )}

          {/* Display Selected Footnote (Editable) */}
          {selectedFootnote && (
            <div className="mt-3 pt-3 border-t border-gray-200 [&_[contenteditable]]:text-[12px]">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Selected Footnote (Editable)
              </label>
              <RichTextEditor
                value={selectedFootnote}
                onChange={setSelectedFootnote}
                placeholder="Edit footnote (use toolbar for bold, italic, underline, strikethrough)"
                minHeight="120px"
              />
            </div>
          )}
        </Card>
      </motion.div>

      {/* Customer Information */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
        <Card padding="sm" className="overflow-hidden">
          <div className="flex items-center mb-2 pb-2 border-b border-gray-200">
            <User className="w-4 h-4 text-blue-600 mr-2" />
            <h2 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
              Customer Information
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <label className="text-xs font-medium text-gray-700">Customer *</label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCreateCustomerModalOpen(true)}
                  icon={<Plus className="w-3 h-3" />}
                >
                  Create New
                </Button>
              </div>
              <select
                value={customerId}
                onChange={(e) => handleCustomerChange(e.target.value)}
                className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                style={{ fontFamily: 'var(--font-family)' }}
              >
                <option value="">Select Customer</option>
                {customers.map((customer) => (
                  <option key={customer._id} value={customer._id}>
                    {customer.name} {customer.company ? `(${customer.company})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {selectedCustomer && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Company</label>
                  <input
                    type="text"
                    value={selectedCustomer.company || '-'}
                    readOnly
                    className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50"
                    style={{ fontFamily: 'var(--font-family)' }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="text"
                    value={selectedCustomer.email || '-'}
                    readOnly
                    className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50"
                    style={{ fontFamily: 'var(--font-family)' }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="text"
                    value={selectedCustomer.phone || '-'}
                    readOnly
                    className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50"
                    style={{ fontFamily: 'var(--font-family)' }}
                  />
                </div>
              </>
            )}
          </div>

          {/* Project Selection */}
          {selectedCustomer && selectedCustomer.projects && selectedCustomer.projects.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Project Name
                </label>
                <select
                  value={projectIndex}
                  onChange={(e) => handleProjectChange(parseInt(e.target.value))}
                  className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  style={{ fontFamily: 'var(--font-family)' }}
                >
                  <option value={-1}>Select Project</option>
                  {selectedCustomer.projects.map((project, idx) => (
                    <option key={idx} value={idx}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedProject && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Project Address
                  </label>
                  <input
                    type="text"
                    value={selectedProject.address || '-'}
                    readOnly
                    className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50"
                    style={{ fontFamily: 'var(--font-family)' }}
                  />
                </div>
              )}
            </div>
          )}
        </Card>
      </motion.div>

      {/* Inquiry of */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }}>
        <Card padding="sm" className="overflow-hidden">
          <div className="flex items-center mb-2 pb-2 border-b border-gray-200">
            <UserCircle className="w-4 h-4 text-blue-600 mr-2" />
            <h2 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
              Inquiry of
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Employee Name</label>
              <select
                value={inquiryEmployeeId}
                onChange={(e) => setInquiryEmployeeId(e.target.value)}
                className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                style={{ fontFamily: 'var(--font-family)' }}
              >
                <option value="">Select Employee</option>
                {employees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Designation</label>
              <input
                type="text"
                value={employees.find((e) => e._id === inquiryEmployeeId)?.designation || ''}
                readOnly
                placeholder="Auto-filled from employee"
                className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50"
                style={{ fontFamily: 'var(--font-family)' }}
              />
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Schedules */}
      {quotationSchedules.map((schedule, scheduleIdx) => (
        <motion.div
          key={schedule.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 + scheduleIdx * 0.1 }}
        >
          <Card padding="sm" className="border border-gray-300 overflow-hidden">
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 text-blue-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
                  Schedule {scheduleIdx + 1}
                </h2>
              </div>
              {quotationSchedules.length > 1 && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => removeSchedule(scheduleIdx)}
                  icon={<Trash2 className="w-3 h-3" />}
                >
                  Remove
                </Button>
              )}
            </div>

            {/* Schedule Selection and Percentage Dropdowns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
              <div className="md:col-span-3">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label className="text-xs font-medium text-gray-700">Schedule *</label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCreateScheduleModalOpen(true)}
                    icon={<Plus className="w-3 h-3" />}
                  >
                    Create New
                  </Button>
                </div>
                <select
                  value={schedule.scheduleId}
                  onChange={(e) => handleScheduleChange(scheduleIdx, e.target.value)}
                  className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Schedule</option>
                  {schedules.map((sch) => (
                    <option key={sch._id} value={sch._id}>
                      {sch.name}
                    </option>
                  ))}
                </select>
              </div>

              {schedule.scheduleDetails?.description && (
                <div className="md:col-span-3">
                  <p className="text-xs text-gray-500 italic break-words overflow-wrap-anywhere">{schedule.scheduleDetails.description}</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Profit Margin (%)</label>
                <input
                  type="number"
                  value={schedule.profitMarginRate ?? ''}
                  onChange={(e) => updateScheduleField(scheduleIdx, 'profitMarginRate', parseFloat(e.target.value) || 0)}
                  className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Customs & Freight (%)</label>
                <input
                  type="number"
                  value={schedule.customsDutyRate ?? ''}
                  onChange={(e) => updateScheduleField(scheduleIdx, 'customsDutyRate', parseFloat(e.target.value) || 0)}
                  className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Other (%)</label>
                <input
                  type="number"
                  value={schedule.other}
                  onChange={(e) => updateScheduleField(scheduleIdx, 'other', parseFloat(e.target.value) || 0)}
                  className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">AIT</label>
                <select
                  value={schedule.aitId}
                  onChange={(e) => updateScheduleField(scheduleIdx, 'aitId', e.target.value)}
                  className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select</option>
                  {aits.map((ait) => (
                    <option key={ait._id} value={ait._id}>
                      {ait.name} ({ait.rate}%)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">VAT</label>
                <select
                  value={schedule.vatId}
                  onChange={(e) => updateScheduleField(scheduleIdx, 'vatId', e.target.value)}
                  className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select</option>
                  {vats.map((vat) => (
                    <option key={vat._id} value={vat._id}>
                      {vat.name} ({vat.rate}%)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">VAT Inclusion</label>
                <div className="flex items-center h-8">
                  <input
                    type="checkbox"
                    id={`vatInclusion-${scheduleIdx}`}
                    checked={schedule.vatInclusion}
                    onChange={(e) => updateScheduleField(scheduleIdx, 'vatInclusion', e.target.checked)}
                    className="w-3.5 h-3.5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-1"
                  />
                  <label htmlFor={`vatInclusion-${scheduleIdx}`} className="ml-2 text-xs text-gray-600">
                    Include VAT in Total
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
                <select
                  value={schedule.currency}
                  onChange={(e) => updateScheduleField(scheduleIdx, 'currency', e.target.value)}
                  className="form-select px-2 py-1.5 text-sm"
                >
                  <option value="BDT">BDT (৳)</option>
                  <option value="USD">USD ($)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">CFR CTG Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={schedule.cfrCtgAmount || 0}
                  onChange={(e) => {
                    const amount = parseFloat(e.target.value) || 0;
                    updateScheduleField(scheduleIdx, 'cfrCtgAmount', amount);
                    updateScheduleField(scheduleIdx, 'cfrCtg', amount > 0);
                  }}
                  placeholder="0.00"
                  className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  style={{ fontFamily: 'var(--font-family)' }}
                />
                <p className="mt-0.5 text-xs text-gray-500">Enter amount to include CFR CTG in total</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <select
                  value={schedule.selectedNoteId || ''}
                  onChange={(e) => {
                    const note = notesList.find(n => n._id === e.target.value);
                    updateScheduleField(scheduleIdx, 'selectedNoteId', e.target.value);
                    updateScheduleField(scheduleIdx, 'notes', note ? note.content : '');
                  }}
                  className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  style={{ fontFamily: 'var(--font-family)' }}
                >
                  <option value="">Select a note (optional)</option>
                  {notesList.map((note) => (
                    <option key={note._id} value={note._id}>
                      {note.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Display Selected Schedule Notes */}
            {schedule.notes && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Selected Note (Editable)
                </label>
                <RichTextEditor
                  value={schedule.notes}
                  onChange={(content) => updateScheduleField(scheduleIdx, 'notes', content)}
                  placeholder="Edit note (use toolbar for bold, italic, underline, strikethrough)"
                  minHeight="100px"
                />
              </div>
            )}

            {/* Sections under Schedule */}
            {schedule.sections.map((section, sectionIdx) => (
              <div key={section.id} className="mt-3 p-2 bg-gray-50 border border-gray-300 rounded">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <Layers className="w-3.5 h-3.5 text-green-600 mr-1.5" />
                    <h3 className="text-xs font-semibold text-gray-900">Section {sectionIdx + 1}</h3>
                  </div>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => removeSection(scheduleIdx, sectionIdx)}
                    icon={<Trash2 className="w-3 h-3" />}
                  >
                    Remove
                  </Button>
                </div>

                <div className="grid grid-cols-1 gap-2 mb-2">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <label className="text-xs font-medium text-gray-700">Section *</label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setCreateSectionModalOpen(true)}
                        icon={<Plus className="w-3 h-3" />}
                      >
                        Create New
                      </Button>
                    </div>
                    <select
                      value={section.sectionId}
                      onChange={(e) => handleSectionChange(scheduleIdx, sectionIdx, e.target.value)}
                      className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 bg-white"
                    >
                      <option value="">Select Section</option>
                      {getSectionsForSchedule(schedule.scheduleId).map((sec) => (
                        <option key={sec._id} value={sec._id}>
                          {sec.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {section.sectionDetails?.description && (
                    <div>
                      <p className="text-xs text-gray-500 italic break-words overflow-wrap-anywhere">{section.sectionDetails.description}</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Customs Duty (%)</label>
                    <input
                      type="number"
                      value={section.customsDutyPercentage ?? ''}
                      onChange={(e) => updateSectionField(scheduleIdx, sectionIdx, 'customsDutyPercentage', e.target.value === '' ? undefined : parseFloat(e.target.value) || 0)}
                      className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 bg-white"
                      placeholder="Optional override"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* Products under Section */}
                {section.products.map((product, productIdx) => (
                  <div key={product.id} className="mt-2 p-2 bg-white border border-gray-200 rounded">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <Package className="w-3 h-3 text-orange-600 mr-1" />
                        <h4 className="text-xs font-semibold text-gray-900">Product {productIdx + 1}</h4>
                      </div>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => removeProduct(scheduleIdx, sectionIdx, productIdx)}
                        icon={<Trash2 className="w-3 h-3" />}
                      >
                        <span className="hidden sm:inline">Remove</span>
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Primary Category</label>
                        <select
                          value={product.primaryCategoryId || ''}
                          onChange={(e) =>
                            handlePrimaryCategoryChange(scheduleIdx, sectionIdx, productIdx, e.target.value)
                          }
                          className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 bg-white"
                        >
                          <option value="">All categories</option>
                          {primaryCategories.map((c) => (
                            <option key={c._id} value={c._id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {product.primaryCategoryId && getSubCategoriesForPrimary(product.primaryCategoryId).length > 0 && (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Sub Category</label>
                          <select
                            value={product.subCategoryId || ''}
                            onChange={(e) =>
                              handleSubCategoryChange(scheduleIdx, sectionIdx, productIdx, e.target.value)
                            }
                            className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 bg-white"
                          >
                            <option value="">All (primary)</option>
                            {getSubCategoriesForPrimary(product.primaryCategoryId).map((c) => (
                              <option key={c._id} value={c._id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="relative">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <label className="text-xs font-medium text-gray-700">Product *</label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setCreateProductModalOpen(true)}
                            icon={<Plus className="w-3 h-3" />}
                          >
                            Create New
                          </Button>
                        </div>
                        {(() => {
                          const isOpen =
                            productSearchOpen?.scheduleIdx === scheduleIdx &&
                            productSearchOpen?.sectionIdx === sectionIdx &&
                            productSearchOpen?.productIdx === productIdx;
                          const productsInSection = getProductsForSection(section.sectionId);
                          const filteredByCat = getFilteredProducts(product.primaryCategoryId, product.subCategoryId, productsInSection);
                          const searchLower = productSearchQuery.trim().toLowerCase();
                          const filteredProducts = searchLower
                            ? filteredByCat.filter(
                                (p) =>
                                  (p.name || '').toLowerCase().includes(searchLower) ||
                                  (p.model || '').toLowerCase().includes(searchLower)
                              )
                            : filteredByCat;
                          const displayLabel = product.productDetails
                            ? `${product.productDetails.name}${product.productDetails.model ? ` - ${product.productDetails.model}` : ''}`
                            : 'Select Product';
                          const listboxId = `product-listbox-${scheduleIdx}-${sectionIdx}-${productIdx}`;
                          return (
                            <>
                              <div
                                role="combobox"
                                aria-expanded={isOpen}
                                aria-haspopup="listbox"
                                aria-controls={listboxId}
                                onClick={() => {
                                  setProductSearchOpen({ scheduleIdx, sectionIdx, productIdx });
                                  setProductSearchQuery('');
                                }}
                                className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 bg-white cursor-pointer text-left flex items-center justify-between"
                              >
                                <span className={product.productId ? 'text-gray-900' : 'text-gray-500'}>
                                  {isOpen && productSearchQuery !== undefined ? productSearchQuery || 'Type to search...' : displayLabel}
                                </span>
                                <span className="text-gray-400">▼</span>
                              </div>
                              {isOpen && (
                                <>
                                  <div
                                    className="absolute z-20 mt-0.5 w-full bg-white border border-gray-300 rounded shadow-lg max-h-56 overflow-hidden"
                                    style={{ minWidth: '100%' }}
                                  >
                                    <input
                                      type="text"
                                      autoFocus
                                      value={productSearchQuery}
                                      onChange={(e) => setProductSearchQuery(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Escape') {
                                          setProductSearchOpen(null);
                                          setProductSearchQuery('');
                                        }
                                      }}
                                      placeholder="Type to search..."
                                      className="w-full px-2 py-1.5 text-sm border-b border-gray-200 focus:outline-none focus:ring-0"
                                    />
                                    <ul
                                      id={listboxId}
                                      role="listbox"
                                      className="max-h-48 overflow-y-auto"
                                    >
                                      {filteredProducts.length === 0 ? (
                                        <li className="px-2 py-2 text-xs text-gray-500">No products match</li>
                                      ) : (
                                        filteredProducts.map((prod) => (
                                          <li
                                            key={prod._id}
                                            role="option"
                                            aria-selected={prod._id === product.productId}
                                            onClick={() => {
                                              handleProductChange(scheduleIdx, sectionIdx, productIdx, prod._id);
                                              setProductSearchOpen(null);
                                              setProductSearchQuery('');
                                            }}
                                            className={`px-2 py-1.5 text-sm cursor-pointer hover:bg-orange-50 ${prod._id === product.productId ? 'bg-orange-100' : ''}`}
                                          >
                                            {prod.name} {prod.model ? `- ${prod.model}` : ''}
                                          </li>
                                        ))
                                      )}
                                    </ul>
                                  </div>
                                  <div
                                    className="fixed inset-0 z-10"
                                    aria-hidden="true"
                                    onClick={() => {
                                      setProductSearchOpen(null);
                                      setProductSearchQuery('');
                                    }}
                                  />
                                </>
                              )}
                            </>
                          );
                        })()}
                      </div>

                      {product.productDetails && (
                        <div className="p-2 bg-gray-50 rounded text-xs border border-gray-200 space-y-1">
                          {product.productDetails.brand && (
                            <p className="break-words overflow-wrap-anywhere">
                              <strong>Brand:</strong> {typeof product.productDetails.brand === 'object' && product.productDetails.brand !== null
                                ? product.productDetails.brand.name
                                : product.productDetails.brand}
                            </p>
                          )}
                          {product.productDetails.model && (
                            <p className="break-words overflow-wrap-anywhere"><strong>Model:</strong> {product.productDetails.model}</p>
                          )}
                          {product.productDetails.type && (
                            <p className="break-words overflow-wrap-anywhere">
                              <strong>Type:</strong> {typeof product.productDetails.type === 'object' && product.productDetails.type !== null
                                ? product.productDetails.type.name
                                : product.productDetails.type}
                            </p>
                          )}
                          {product.productDetails.compressorType && (
                            <p className="break-words overflow-wrap-anywhere">
                              <strong>Compressor Type:</strong> {typeof product.productDetails.compressorType === 'object' && product.productDetails.compressorType !== null
                                ? product.productDetails.compressorType.name
                                : product.productDetails.compressorType}
                            </p>
                          )}
                          {product.productDetails.countryOfOrigin && (
                            <p className="break-words overflow-wrap-anywhere">
                              <strong>Country of Origin:</strong> {typeof product.productDetails.countryOfOrigin === 'object' && product.productDetails.countryOfOrigin !== null
                                ? product.productDetails.countryOfOrigin.name
                                : product.productDetails.countryOfOrigin}
                            </p>
                          )}
                          {product.productDetails.countryOfAssembly && (
                            <p className="break-words overflow-wrap-anywhere">
                              <strong>Country of Assembly:</strong> {typeof product.productDetails.countryOfAssembly === 'object' && product.productDetails.countryOfAssembly !== null
                                ? product.productDetails.countryOfAssembly.name
                                : product.productDetails.countryOfAssembly}
                            </p>
                          )}
                          {product.productDetails.details && (
                            <p className="break-words overflow-wrap-anywhere"><strong>Details:</strong> {product.productDetails.details}</p>
                          )}
                          {product.productDetails.description && (
                            <p className="break-words overflow-wrap-anywhere"><strong>Description:</strong> {product.productDetails.description}</p>
                          )}
                          {product.productDetails.specifications && (
                            <p className="break-words overflow-wrap-anywhere"><strong>Specifications:</strong> {product.productDetails.specifications}</p>
                          )}
                          {product.productDetails.coolingCapacity && (
                            <p className="break-words overflow-wrap-anywhere"><strong>Cooling Capacity:</strong> {product.productDetails.coolingCapacity}</p>
                          )}
                          {product.productDetails.serialNo && (
                            <p className="break-words overflow-wrap-anywhere"><strong>Serial No:</strong> {product.productDetails.serialNo}</p>
                          )}
                          {product.productDetails.partNo && (
                            <p className="break-words overflow-wrap-anywhere"><strong>Part No:</strong> {product.productDetails.partNo}</p>
                          )}
                        </div>
                      )}

                      {product.productId && (
                        <>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            <div>
                              <label className="block text-[10px] font-medium text-gray-700 mb-0.5">
                                Unit Purchase Rate
                              </label>
                              <input
                                type="number"
                                value={product.unitPurchaseRate}
                                onChange={(e) =>
                                  updateProductField(
                                    scheduleIdx,
                                    sectionIdx,
                                    productIdx,
                                    'unitPurchaseRate',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                className="block w-full px-1.5 py-1 text-xs border border-gray-300 rounded"
                                min="0"
                                step="0.01"
                              />
                            </div>

                            <div>
                              <label className="block text-[10px] font-medium text-gray-700 mb-0.5">Qty</label>
                              <input
                                type="number"
                                value={product.qty === '' ? '' : product.qty}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (raw === '') {
                                    updateProductField(scheduleIdx, sectionIdx, productIdx, 'qty', '');
                                    return;
                                  }
                                  const n = parseFloat(raw);
                                  updateProductField(scheduleIdx, sectionIdx, productIdx, 'qty', Number.isFinite(n) && n >= 0 ? n : 0);
                                }}
                                className="block w-full px-1.5 py-1 text-xs border border-gray-300 rounded"
                                min="0"
                              />
                            </div>

                            <div>
                              <label className="block text-[10px] font-medium text-gray-700 mb-0.5">Unit</label>
                              {(() => {
                                const { singleUnit, options } = getProductUnitOptions(product.productDetails, units);
                                if (singleUnit != null) {
                                  return (
                                    <input
                                      type="text"
                                      readOnly
                                      value={product.unit || singleUnit}
                                      className="block w-full px-1.5 py-1 text-xs border border-gray-200 rounded bg-gray-100"
                                    />
                                  );
                                }
                                const unitOptions = options.length > 0 ? options : (Array.isArray(units) ? units.map((u) => ({ value: u.symbol || u.name || '', label: u.symbol ? `${u.name} (${u.symbol})` : u.name || '' })) : []);
                                return (
                                  <select
                                    value={product.unit || ''}
                                    onChange={(e) =>
                                      updateProductField(scheduleIdx, sectionIdx, productIdx, 'unit', e.target.value)
                                    }
                                    className="block w-full px-1.5 py-1 text-xs border border-gray-300 rounded"
                                  >
                                    <option value="">Select Unit</option>
                                    {unitOptions.map((opt, idx) => (
                                      <option key={opt.value + idx} value={opt.value}>
                                        {opt.label || opt.value}
                                      </option>
                                    ))}
                                  </select>
                                );
                              })()}
                            </div>

                            <div>
                              <label className="block text-[10px] font-medium text-gray-700 mb-0.5">
                                Discount
                              </label>
                              <div className="flex space-x-1">
                                <input
                                  type="number"
                                  value={product.discount}
                                  onChange={(e) =>
                                    updateProductField(
                                      scheduleIdx,
                                      sectionIdx,
                                      productIdx,
                                      'discount',
                                      parseFloat(e.target.value) || 0
                                    )
                                  }
                                  className="block w-full px-1.5 py-1 text-xs border border-gray-300 rounded"
                                  min="0"
                                />
                                <select
                                  value={product.discountType}
                                  onChange={(e) =>
                                    updateProductField(
                                      scheduleIdx,
                                      sectionIdx,
                                      productIdx,
                                      'discountType',
                                      e.target.value
                                    )
                                  }
                                  className="px-1 py-1 text-xs border border-gray-300 rounded"
                                >
                                  <option value="percentage">%</option>
                                  <option value="amount">৳</option>
                                </select>
                              </div>
                            </div>

                            <div>
                              <label className="block text-[10px] font-medium text-gray-700 mb-0.5">Customs Duty (%)</label>
                              <input
                                type="number"
                                value={product.customsDutyPercentage ?? ''}
                                onChange={(e) =>
                                  updateProductField(
                                    scheduleIdx,
                                    sectionIdx,
                                    productIdx,
                                    'customsDutyPercentage',
                                    e.target.value === '' ? undefined : parseFloat(e.target.value) || 0
                                  )
                                }
                                className="block w-full px-1.5 py-1 text-xs border border-gray-300 rounded"
                                placeholder="Optional"
                                min="0"
                                step="0.01"
                              />
                            </div>
                          </div>

                          {/* Calculated Values */}
                          {(() => {
                            const calc = calculateProductTotals(scheduleIdx, product, section);
                            const currencySymbol = schedule.currency === 'USD' ? '$' : '৳';
                            return (
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 p-1.5 bg-blue-50 border border-blue-200 rounded text-[10px]">
                                <div className="break-words">
                                  <span className="font-medium">Profit:</span> {currencySymbol}{calc.profitMarginAmount.toFixed(2)}
                                </div>
                                <div className="break-words">
                                  <span className="font-medium">Customs:</span> {currencySymbol}{calc.customsDutyAmount.toFixed(2)}
                                </div>
                                <div className="break-words">
                                  <span className="font-medium">Other:</span> {currencySymbol}{calc.otherAmount.toFixed(2)}
                                </div>
                                <div className="break-words">
                                  <span className="font-medium">AIT:</span> {currencySymbol}{calc.aitAmount.toFixed(2)}
                                </div>
                                <div className="break-words">
                                  <span className="font-medium">Unit Rate:</span> {currencySymbol}{calc.unitRate.toFixed(2)}
                                </div>
                                <div className="break-words">
                                  <span className="font-medium">VAT/Rate:</span> {currencySymbol}{calc.vatOnRate.toFixed(2)}
                                </div>
                                <div className="break-words font-bold">
                                  <span className="font-bold">Total:</span> {currencySymbol}{calc.total.toFixed(2)}
                                </div>
                                <div className="break-words">
                                  <span className="font-medium">VAT Amt:</span> {currencySymbol}{calc.vatAmount.toFixed(2)}
                                </div>
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  </div>
                ))}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addProduct(scheduleIdx, sectionIdx)}
                  icon={<Plus className="w-3 h-3" />}
                  className="mt-2"
                >
                  Add Product
                </Button>
              </div>
            ))}

            <Button
              variant="outline"
              size="sm"
              onClick={() => addSection(scheduleIdx)}
              icon={<Plus className="w-3 h-3" />}
              className="mt-3"
            >
              Add Section
            </Button>

            {/* Schedule Summary */}
            {schedule.sections.length > 0 && schedule.sections.some(s => s.products.length > 0) && (
              <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-300 rounded-lg">
                <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-center">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center mr-2 text-xs">
                    Σ
                  </span>
                  Schedule {scheduleIdx + 1} Summary
                </h3>
                
                {(() => {
                  const scheduleTotals = calculateScheduleTotals(scheduleIdx);
                  const currencySymbol = schedule.currency === 'USD' ? '$' : '৳';
                  const currencyText = schedule.currency === 'USD' ? 'Dollars' : 'Taka';
                  return (
                    <div className="space-y-2">
                      {/* Totals Grid - Single Row */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <div className="bg-white p-2 rounded border border-blue-200">
                          <p className="text-xs text-gray-600 mb-0.5">Total Amount</p>
                          <p className="text-lg font-bold text-gray-900">
                            {currencySymbol}{scheduleTotals.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </div>
                        
                        <div className="bg-white p-2 rounded border border-blue-200">
                          <p className="text-xs text-gray-600 mb-0.5">VAT Amount</p>
                          <p className="text-lg font-bold text-gray-900">
                            {currencySymbol}{scheduleTotals.totalVatAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </div>

                        {/* Total with VAT Included or Subtotal */}
                        {schedule.vatInclusion ? (
                          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-2 rounded border border-blue-200">
                            <p className="text-xs text-gray-600 mb-0.5">Subtotal (VAT Included{scheduleTotals.cfrCtgAmount > 0 ? ', Before CFR CTG' : ''})</p>
                            <p className="text-lg font-bold text-gray-900">
                              {currencySymbol}{scheduleTotals.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>
                        ) : (
                          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-2 rounded border border-blue-200">
                            <p className="text-xs text-gray-600 mb-0.5">Subtotal (Before VAT & CFR CTG)</p>
                            <p className="text-lg font-bold text-gray-900">
                              {currencySymbol}{(scheduleTotals.totalAmount + scheduleTotals.totalVatAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* CFR CTG Amount Display - Before Grand Total */}
                      {scheduleTotals.cfrCtgAmount > 0 && (
                        <div className="bg-yellow-50 p-2 rounded border border-yellow-300 mt-2">
                          <div className="flex justify-between items-center">
                            <p className="text-xs font-medium text-gray-700">CFR CTG</p>
                            <p className="text-sm font-bold text-gray-900">
                              {currencySymbol}{scheduleTotals.cfrCtgAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Grand Total - After CFR CTG */}
                      {/* Show grand total if: VAT not included OR (VAT included AND CFR CTG > 0) */}
                      {(!schedule.vatInclusion || scheduleTotals.cfrCtgAmount > 0) && (
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-2 rounded border-2 border-green-400 mt-2">
                          <div className="flex justify-between items-center">
                            <p className="text-xs font-bold text-gray-700">
                              Grand Total
                              {schedule.vatInclusion && scheduleTotals.cfrCtgAmount > 0 ? ' (Including CFR CTG)' : ''}
                              {!schedule.vatInclusion ? ` (Including VAT${scheduleTotals.cfrCtgAmount > 0 ? ' & CFR CTG' : ''})` : ''}
                            </p>
                            <p className="text-lg font-bold text-green-700">
                              {currencySymbol}{scheduleTotals.grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Amount in Words */}
                      <div className="bg-white p-2 rounded border border-blue-300">
                        <p className="text-xs font-medium text-gray-700 mb-1">Amount in Words:</p>
                        <p className="text-sm font-semibold text-blue-900 italic break-words overflow-wrap-anywhere">
                          {numberToWords(scheduleTotals.grandTotal)} {currencyText}
                        </p>
                      </div>

                      {schedule.vatInclusion && (
                        <p className="text-xs text-gray-600 italic text-center">
                          * VAT is included in the total amount
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </Card>
        </motion.div>
      ))}

      {/* Quotation Summary */}
      {quotationSchedules.length > 0 && quotationSchedules.some(s => s.scheduleId) && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card padding="sm" className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300">
            <div className="flex items-center mb-3 pb-2 border-b border-blue-300">
              <Package className="w-5 h-5 text-blue-700 mr-2" />
              <h2 className="text-base font-bold text-blue-900" style={{ fontFamily: 'var(--font-family)' }}>
                Quotation Summary
              </h2>
            </div>
            
            {(() => {
              const summary = calculateQuotationSummary();
              const hasBDT = summary.overallByCurrency.BDT.grandTotal > 0;
              const hasUSD = summary.overallByCurrency.USD.grandTotal > 0;
              
              return (
                <div className="space-y-4">
                  {/* Schedule-based Summary Table */}
                  <div className="bg-white rounded-lg border border-blue-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-blue-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Schedule</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Name</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">Subtotal</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">Total Amount</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">VAT Amount</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">Grand Total</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {summary.schedules.map((schedule, idx) => {
                            const currencySymbol = schedule.currency === 'BDT' ? '৳' : '$';
                            const textColor = schedule.currency === 'BDT' ? 'text-blue-900' : 'text-green-900';
                            
                            return (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-3 py-2 text-xs font-medium text-gray-900">
                                  Schedule {String.fromCharCode(65 + idx)}
                                </td>
                                <td className="px-3 py-2 text-xs text-gray-700">
                                  {schedule.scheduleName}
                                </td>
                                <td className={`px-3 py-2 text-xs font-medium text-right ${textColor}`}>
                                  {currencySymbol}{schedule.subtotal.toFixed(2)}
                                </td>
                                <td className={`px-3 py-2 text-xs font-medium text-right ${textColor}`}>
                                  {currencySymbol}{schedule.totalAmount.toFixed(2)}
                                </td>
                                <td className="px-3 py-2 text-xs font-medium text-right text-orange-600">
                                  {currencySymbol}{schedule.vatAmount.toFixed(2)}
                                </td>
                                <td className={`px-3 py-2 text-sm font-bold text-right ${textColor}`}>
                                  {currencySymbol}{schedule.grandTotal.toFixed(2)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Overall Totals by Currency */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {hasBDT && (
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-lg border-2 border-blue-300">
                        <h3 className="text-sm font-bold text-blue-900 mb-2">Overall BDT (৳) Total</h3>
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-700">Grand Total:</span>
                            <span className="font-bold text-blue-900">৳{summary.overallByCurrency.BDT.grandTotal.toFixed(2)}</span>
                          </div>
                          <div className="pt-2 border-t border-blue-300">
                            <p className="text-xs text-gray-600 mb-1">In Words:</p>
                            <p className="text-xs font-semibold text-blue-900 italic">
                              {numberToWords(summary.overallByCurrency.BDT.grandTotal)} Taka
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {hasUSD && (
                      <div className="bg-gradient-to-br from-green-50 to-green-100 p-3 rounded-lg border-2 border-green-300">
                        <h3 className="text-sm font-bold text-green-900 mb-2">Overall USD ($) Total</h3>
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-700">Grand Total:</span>
                            <span className="font-bold text-green-900">${summary.overallByCurrency.USD.grandTotal.toFixed(2)}</span>
                          </div>
                          <div className="pt-2 border-t border-green-300">
                            <p className="text-xs text-gray-600 mb-1">In Words:</p>
                            <p className="text-xs font-semibold text-green-900 italic">
                              {numberToWords(summary.overallByCurrency.USD.grandTotal)} Dollars
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-2 border-t border-blue-200">
                    <div className="text-center">
                      <p className="text-xs text-gray-600">Total Schedules</p>
                      <p className="text-base font-bold text-gray-900">{quotationSchedules.length}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-600">Total Sections</p>
                      <p className="text-base font-bold text-gray-900">
                        {quotationSchedules.reduce((sum, s) => sum + s.sections.length, 0)}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-600">Total Products</p>
                      <p className="text-base font-bold text-gray-900">
                        {quotationSchedules.reduce((sum, s) => 
                          sum + s.sections.reduce((sSum, sec) => sSum + sec.products.length, 0), 0
                        )}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-600">Currencies Used</p>
                      <p className="text-base font-bold text-blue-700">
                        {hasBDT && hasUSD ? 'BDT & USD' : hasBDT ? 'BDT' : 'USD'}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}
          </Card>
        </motion.div>
      )}

      <Button size="sm" onClick={addSchedule} icon={<Plus className="w-3 h-3" />} className="w-full md:w-auto">
        Add Schedule
      </Button>

      <CreateCustomerModal
        isOpen={createCustomerModalOpen}
        onClose={() => setCreateCustomerModalOpen(false)}
        onSuccess={(customer) => {
          fetchAllData({ silent: true });
          if (customer?._id) {
            setCustomerId(customer._id);
            setSelectedCustomer(customer);
          }
        }}
      />
      <CreateScheduleModal
        isOpen={createScheduleModalOpen}
        onClose={() => setCreateScheduleModalOpen(false)}
        onSuccess={() => {
          fetchAllData({ silent: true });
        }}
      />
      <CreateSectionModal
        isOpen={createSectionModalOpen}
        onClose={() => setCreateSectionModalOpen(false)}
        onSuccess={() => {
          fetchAllData({ silent: true });
        }}
      />
      <CreateProductModal
        isOpen={createProductModalOpen}
        onClose={() => setCreateProductModalOpen(false)}
        onSuccess={() => {
          fetchAllData({ silent: true });
        }}
      />
    </div>
  );
};

export default CreateQuotationPage;

